import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2, X, Play, Layers, Grid3X3, List, Edit, MessageSquare } from 'lucide-react';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { PlaybookFilters } from '../components/PlaybookFilters';
import { useToast } from "../components/ui/Toast";
import { v4 as uuidv4 } from 'uuid';
import { Playbook } from '../types';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { usePlaybookFilters } from '../hooks/usePlaybookFilters';

const ITEMS_PER_PAGE = 20;

export const InteractivePlaybooksList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // New filtering system - interactive playbooks only
  const playbookFilters = usePlaybookFilters('interactive', false);
  
  // UI state
  const [displayedPlaybooks, setDisplayedPlaybooks] = useState<Playbook[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  
  const [sessionId, setSessionId] = useState<string>(uuidv4());

  // Update displayed playbooks when filter results change
  useEffect(() => {
    setDisplayedPlaybooks(playbookFilters.filteredPlaybooks.slice(0, ITEMS_PER_PAGE));
    setHasMore(playbookFilters.filteredPlaybooks.length > ITEMS_PER_PAGE);
  }, [playbookFilters.filteredPlaybooks]);
  
  // Show error toast if filtering fails
  useEffect(() => {
    if (playbookFilters.error) {
      addToast({
        title: "Error",
        description: playbookFilters.error,
        variant: "destructive",
      });
    }
  }, [playbookFilters.error, addToast]);

  const loadMorePlaybooks = useCallback(() => {
    if (playbookFilters.hasActiveFilters || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedPlaybooks.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    if (startIndex >= playbookFilters.filteredPlaybooks.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = playbookFilters.filteredPlaybooks.slice(startIndex, endIndex);
    setDisplayedPlaybooks(prev => [...prev, ...newItems]);

    if (endIndex >= playbookFilters.filteredPlaybooks.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  }, [playbookFilters.hasActiveFilters, playbookFilters.filteredPlaybooks, loadingMore, hasMore, displayedPlaybooks.length]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !playbookFilters.hasActiveFilters) {
        loadMorePlaybooks();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, playbookFilters.hasActiveFilters, loadMorePlaybooks]);

  const handleViewPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/view/${playbookId}`);
  };

  const handleEditPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/edit/${playbookId}`);
  };

  const handleChatClick = (playbook: Playbook, sessionId: string) => {
    navigate(`/studio/chat`, { 
      state: { 
        playbookId: playbook.playbookId,
        playbookName: playbook.playbookName,
        sessionId: sessionId
      } 
    });
  };



  const handleDeletePlaybook = async (playbookId: string) => {
    setIsDeletingId(playbookId);
    try {
      await playbooksApi.deletePlaybook(playbookId);

      addToast({
        title: "Success",
        description: "Interactive playbook deleted successfully",
        variant: "default",
      });

      // Refresh playbooks using the new filtering system
      await playbookFilters.refreshPlaybooks();
    } catch (error) {
      console.error('Error deleting playbook:', error);
      addToast({
        title: "Error", 
        description: "Failed to delete playbook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setPlaybookToDelete(null);
    }
  };

  const scrollToTop = () => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollTop } = containerRef.current;
        setShowScrollTop(scrollTop > 300);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const renderPlaybookCard = (playbook: Playbook, sessionId: string) => (
    <Card key={playbook.playbookId} className="group hover:shadow-2xl transition-all duration-300 border-purple-200 hover:border-purple-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-purple-300/40 bg-gradient-to-br from-white to-purple-50/30">
      <CardContent className="p-4">
        <div className="flex flex-col">
          {/* Top section - Icon and Info */}
          <div className="flex items-start gap-4 mb-4">
            {/* Dialogue Icon */}
            <div className="bg-gradient-to-br from-purple-100 to-fuchsia-200 p-3 rounded-xl flex-shrink-0 shadow-sm border border-purple-100">
              <MessageSquare className="h-6 w-6 text-purple-600" />
            </div>
            
            {/* Dialogue Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate tracking-tight">
                  {playbook.playbookName}
                </h3>
                {playbook.playbookProductName && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-fuchsia-500 text-white shadow-sm flex-shrink-0">
                    {playbook.playbookProductName}
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-600 min-h-[40px]">
                {playbook.playbookDescription && playbook.playbookDescription.length > 300 
                  ? `${playbook.playbookDescription.substring(0, 300)}...` 
                  : playbook.playbookDescription || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Tags section - before buttons */}
          {playbook.playbookTags && playbook.playbookTags.length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-100">
              <div className="flex flex-wrap gap-1">
                {playbook.playbookTags.slice(0, 4).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
                {playbook.playbookTags.length > 4 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    +{playbook.playbookTags.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Bottom section - Actions */}
          <div className="flex items-center justify-center gap-3 pt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewPlaybook(playbook.playbookId)}
              className="border-2 border-purple-300 text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="View Dialogue"
              disabled={isDeletingId === playbook.playbookId}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditPlaybook(playbook.playbookId)}
              className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="Edit Dialogue"
              disabled={isDeletingId === playbook.playbookId}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChatClick(playbook, sessionId)}
              className="border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="Chat with Dialogue"
              disabled={isDeletingId === playbook.playbookId}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Chat
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPlaybookToDelete(playbook.playbookId);
                setDeleteDialogOpen(true);
              }}
              className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200 px-4 py-2 font-semibold"
              disabled={isDeletingId === playbook.playbookId}
              title="Delete Dialogue"
            >
              {isDeletingId === playbook.playbookId ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderPlaybookListItem = (playbook: Playbook) => (
    <Card key={playbook.playbookId} className="bg-gradient-to-r from-white to-purple-50/30 border border-purple-200 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.01]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Dialogue Icon */}
            <div className="bg-gradient-to-br from-purple-100 to-fuchsia-200 p-2 rounded-lg flex-shrink-0 shadow-sm">
              <MessageSquare className="h-5 w-5 text-purple-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                  {playbook.playbookName}
                </h3>
              {playbook.playbookProductName && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-fuchsia-500 text-white shadow-sm flex-shrink-0">
                  {playbook.playbookProductName}
                </span>
              )}
              </div>
                          <p className="text-sm text-gray-600 mt-1">
              {playbook.playbookDescription && playbook.playbookDescription.length > 300 
                ? `${playbook.playbookDescription.substring(0, 300)}...` 
                : playbook.playbookDescription || 'No description provided.'}
            </p>
            </div>
          </div>
          
          {/* Actions at bottom */}
          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewPlaybook(playbook.playbookId)}
              className="border-2 border-purple-300 text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === playbook.playbookId}
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditPlaybook(playbook.playbookId)}
              className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === playbook.playbookId}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChatClick(playbook, sessionId)}
              className="border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === playbook.playbookId}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Chat
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPlaybookToDelete(playbook.playbookId);
                setDeleteDialogOpen(true);
              }}
              disabled={isDeletingId === playbook.playbookId}
              className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200 px-3 py-2 font-semibold"
            >
              {isDeletingId === playbook.playbookId ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Use filtered playbooks for display
  const currentPlaybooks = playbookFilters.hasActiveFilters 
    ? playbookFilters.filteredPlaybooks 
    : displayedPlaybooks;
  const currentTotal = playbookFilters.filteredPlaybooks.length;

  // Calculate stats
  const stats = {
    total: playbookFilters.playbooks.length,
    active: currentPlaybooks.length,
    products: new Set(playbookFilters.playbooks.filter(p => p.playbookProductName).map(p => p.playbookProductName)).size,
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased" ref={containerRef}>
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-violet-500 to-fuchsia-700"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/30 via-transparent to-fuchsia-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-fuchsia-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Dialogues
                  </h1>
                  <p className="text-purple-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Create interactive conversations and dynamic dialogues that enable real-time interactions with your agents
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Dialogues
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-fuchsia-200">{stats.active}</span> Available
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-purple-200">{stats.products}</span> Products
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/studio/interactive-playbooks/add')}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Create Dialogue</span>
                </div>
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="mb-6">
        <PlaybookFilters filters={playbookFilters} />
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* View Mode Toggle */}
        <div className="flex items-center ml-auto">
          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-purple-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-purple-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
                  {playbookFilters.hasActiveFilters ? (
          <>Found {currentPlaybooks.length || 0} filtered results</>
        ) : (
          <>Showing {currentPlaybooks.length} of {currentTotal} dialogues</>
        )}
        </p>
      </div>

      {/* Playbooks Grid/List */}
      <div className={
        viewMode === 'grid' 
          ? 'grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2' 
          : 'space-y-3'
      }>
        {currentPlaybooks.map(playbook => 
          viewMode === 'grid' ? renderPlaybookCard(playbook, sessionId) : renderPlaybookListItem(playbook)
        )}
      </div>

      {playbookFilters.loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Spinner className="w-8 h-8 text-purple-500" />
                      <p className="text-gray-600">Loading dialogues...</p>
        </div>
      ) : currentPlaybooks.length === 0 && (
        <Card className="bg-white border-2 border-dashed border-gray-300 rounded-lg">
          <CardContent className="flex flex-col items-center justify-center text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {playbookFilters.hasActiveFilters ? 'No dialogues match your filters' : 'No dialogues yet'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm">
              {playbookFilters.hasActiveFilters 
                ? 'Try adjusting your filters to find what you\'re looking for.'
                : 'Get started by creating your first dialogue for interactive conversations.'
              }
            </p>
            {!playbookFilters.hasActiveFilters && (
              <Button
                onClick={() => navigate('/studio/playbooks/add')}
                className="flex items-center gap-2"
              >
                                  <Plus className="w-4 h-4" />
                  Create Dialogue
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Load More */}
      {!playbookFilters.hasActiveFilters && hasMore && !playbookFilters.loading && (
        <div id="scroll-loader" className="flex justify-center py-8">
          {loadingMore ? (
            <div className="flex items-center gap-2">
              <Spinner className="w-5 h-5" />
                              <span className="text-gray-600">Loading more dialogues...</span>
            </div>
          ) : (
            <div className="h-4" />
          )}
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 z-50"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setPlaybookToDelete(null);
        }}
        onConfirm={() => playbookToDelete && handleDeletePlaybook(playbookToDelete)}
        title="Delete Dialogue"
        description="Are you sure you want to delete this dialogue? This action cannot be undone."
        confirmLabel="Delete"
      />


    </div>
  );
};

export default InteractivePlaybooksList;